import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import { MOCK_DRES } from '../dados-simulados';

import type {
  ChaveDaConversa,
  ComentarioDaTratativa,
  FiltroDeTratativas,
  SituacaoDaTratativa,
  TratativaLinha,
} from './contrato';

/**
 * A camada de dados das tratativas SME ↔ DRE.
 *
 * Mesmo desenho das outras listagens: nenhum componente chama `chamarApi`
 * direto e a chave de cache mora junto de quem a usa (CLAUDE.md, "Regras de
 * API").
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. Não existe
 * módulo de tratativas em `apps/api/src/modulos/`: o modelo de dados já está
 * migrado (`apontamento_comentarios`, `apontamento_comentario_respostas` e a
 * função `get_sme_apontamento_tratativas`, em `db/migrations/0002`), mas nenhuma
 * rota o serve. Decisão do dono de 2026-09-22 (CLAUDE.md, "Proibido", exceção
 * estendida ao portal SME) para demonstração à SMT/DTP — mesmas três condições
 * de sempre: só leitura, dado claramente fictício, removido antes de `develop`
 * → `hml` (`git grep -n "TEMPORÁRIO" apps/web/src`, checklist em
 * `docs/ALVOS.md`).
 *
 * A LISTAGEM DERIVA DA CONVERSA, e não o contrário. No banco a listagem é uma
 * função que agrega `apontamento_comentarios` com a última mensagem; aqui ela
 * agrega o mesmo array que a tela de detalhe lê. Duas listas soltas acabariam
 * mostrando uma última mensagem na grade e outra ao abrir a linha.
 */

export const CHAVES_DE_TRATATIVAS = {
  lista: (filtro: FiltroDeTratativas) => ['tratativas', 'lista', filtro] as const,
  conversa: (chave: ChaveDaConversa) => ['tratativas', 'conversa', chave] as const,
};

/**
 * Data no passado, contada a partir do relógio de quem executa.
 *
 * Nenhuma data literal nos mocks: data fixa envelhece sozinha e um dia esvazia
 * a tela — e derruba o teste numa PR que não tem nada a ver com ela.
 */
function horasAtras(horas: number): string {
  return new Date(Date.now() - horas * 3_600_000).toISOString();
}

/** Competência de referência dos mocks: o mês anterior ao de quem executa. */
function competenciaAnterior(): { ano: number; mes: number } {
  const hoje = new Date();
  const anterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return { ano: anterior.getFullYear(), mes: anterior.getMonth() + 1 };
}

const { ano: ANO, mes: MES } = competenciaAnterior();

interface OrdemDeServicoMock {
  ordemServicoId: string;
  osNumero: string;
  dreId: string;
  credenciadoId: string;
  credenciadoNome: string;
  credenciadoDocumento: string;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Nomes de empresa claramente fictícios, iguais aos já usados no restante do
// módulo financeiro (`dados-simulados.ts`) para que a mesma demonstração não
// mostre um parque de credenciados diferente a cada tela.
const ORDENS_MOCK: OrdemDeServicoMock[] = [
  {
    ordemServicoId: 'os-tratativa-01',
    osNumero: 'OS-2026-000181',
    dreId: 'dre-1',
    credenciadoId: 'cred-01',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    credenciadoDocumento: '11.222.333/0001-44',
  },
  {
    ordemServicoId: 'os-tratativa-02',
    osNumero: 'OS-2026-000207',
    dreId: 'dre-7',
    credenciadoId: 'cred-02',
    credenciadoNome: 'Viação Escolar Zona Leste',
    credenciadoDocumento: '22.333.444/0001-55',
  },
  {
    ordemServicoId: 'os-tratativa-03',
    osNumero: 'OS-2026-000233',
    dreId: 'dre-2',
    credenciadoId: 'cred-03',
    credenciadoNome: 'Cooperativa de Vans SP',
    credenciadoDocumento: '33.444.555/0001-66',
  },
  {
    ordemServicoId: 'os-tratativa-04',
    osNumero: 'OS-2026-000258',
    dreId: 'dre-11',
    credenciadoId: 'cred-04',
    credenciadoNome: 'Van Escolar Beta Ltda',
    credenciadoDocumento: '44.555.666/0001-77',
  },
  {
    ordemServicoId: 'os-tratativa-05',
    osNumero: 'OS-2026-000294',
    dreId: 'dre-13',
    credenciadoId: 'cred-05',
    credenciadoNome: 'João Motorista ME',
    credenciadoDocumento: '55.666.777/0001-88',
  },
];

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Cobre as três situações e as duas autorias, para que cada indicador e cada
// opção do filtro tenha pelo menos uma linha correspondente.
const COMENTARIOS_MOCK: ComentarioDaTratativa[] = [
  {
    id: 'tratativa-01',
    ordemServicoId: 'os-tratativa-01',
    ano: ANO,
    mes: MES,
    sublinha: 'NCAD',
    autorPapel: 'SME',
    mensagem:
      'A quantidade de alunos não cadeirantes lançada nos dias 12 e 13 está acima da capacidade registrada na OS. Favor conferir o lançamento ou anexar a autorização de reforço.',
    situacao: 'ABERTO',
    criadoEm: horasAtras(30),
    resolvidoEm: null,
    respostas: [],
  },
  {
    id: 'tratativa-02',
    ordemServicoId: 'os-tratativa-02',
    ano: ANO,
    mes: MES,
    sublinha: 'FALTAS',
    autorPapel: 'SME',
    mensagem:
      'As faltas de alunos do dia 07 estão sem justificativa. Sem ela o desconto entra integral na medição.',
    situacao: 'RESPONDIDO',
    criadoEm: horasAtras(76),
    resolvidoEm: null,
    respostas: [
      {
        id: 'resposta-02-1',
        autorPapel: 'DRE',
        mensagem:
          'Justificativa registrada: a escola suspendeu as aulas do período da tarde por falta de água. Ofício anexado ao processo da competência.',
        criadoEm: horasAtras(20),
      },
    ],
  },
  {
    id: 'tratativa-03',
    ordemServicoId: 'os-tratativa-03',
    ano: ANO,
    mes: MES,
    sublinha: 'FALTA_VEICULO',
    autorPapel: 'DRE',
    mensagem:
      'O veículo não operou nos dias 18 e 19 por manutenção corretiva. Pedimos orientação sobre o desconto proporcional antes do fechamento.',
    situacao: 'RESPONDIDO',
    criadoEm: horasAtras(120),
    resolvidoEm: null,
    respostas: [
      {
        id: 'resposta-03-1',
        autorPapel: 'SME',
        mensagem:
          'O desconto é proporcional aos dias letivos não atendidos. Registre a substituição do veículo, se houver, para que o cálculo considere só os dias sem cobertura.',
        criadoEm: horasAtras(96),
      },
      {
        id: 'resposta-03-2',
        autorPapel: 'DRE',
        mensagem: 'Não houve substituição. Mantidos os dois dias sem cobertura.',
        criadoEm: horasAtras(90),
      },
    ],
  },
  {
    id: 'tratativa-04',
    ordemServicoId: 'os-tratativa-04',
    ano: ANO,
    mes: MES,
    sublinha: 'CAD',
    autorPapel: 'SME',
    mensagem:
      'Divergência entre os cadeirantes lançados e o previsto na OS. Confirme se houve inclusão de aluno no meio da competência.',
    situacao: 'RESOLVIDO',
    criadoEm: horasAtras(300),
    resolvidoEm: horasAtras(210),
    respostas: [
      {
        id: 'resposta-04-1',
        autorPapel: 'DRE',
        mensagem:
          'Houve inclusão de um aluno a partir do dia 09, com laudo entregue à unidade. Lançamento conferido e mantido.',
        criadoEm: horasAtras(240),
      },
    ],
  },
  {
    id: 'tratativa-05',
    ordemServicoId: 'os-tratativa-05',
    ano: ANO,
    mes: MES,
    sublinha: 'NC_COMP',
    autorPapel: 'SME',
    mensagem:
      'A atividade complementar lançada não consta no calendário da unidade. Anexe a autorização para que o valor entre na medição.',
    situacao: 'ABERTO',
    criadoEm: horasAtras(9),
    resolvidoEm: null,
    respostas: [],
  },
  {
    id: 'tratativa-06',
    ordemServicoId: 'os-tratativa-01',
    ano: ANO,
    mes: MES,
    sublinha: 'CAD_CONT',
    autorPapel: 'DRE',
    mensagem:
      'Pedimos revisão do bloqueio da atividade continuada: o transporte foi executado nos cinco dias, com lista de presença assinada.',
    situacao: 'ABERTO',
    criadoEm: horasAtras(52),
    resolvidoEm: null,
    respostas: [],
  },
];

/** A OS de uma tratativa. O mock é fechado, então a ausência seria defeito de dado, não situação de tela. */
function ordemDa(comentario: ComentarioDaTratativa): OrdemDeServicoMock | undefined {
  return ORDENS_MOCK.find((os) => os.ordemServicoId === comentario.ordemServicoId);
}

/** A última coisa dita na conversa — a resposta mais recente ou, sem respostas, a abertura. */
function ultimaFala(comentario: ComentarioDaTratativa) {
  const ultima = comentario.respostas.at(-1);
  return ultima
    ? { mensagem: ultima.mensagem, em: ultima.criadoEm, autorPapel: ultima.autorPapel }
    : {
        mensagem: comentario.mensagem,
        em: comentario.criadoEm,
        autorPapel: comentario.autorPapel,
      };
}

/**
 * Monta a linha da grade a partir da conversa, do mesmo jeito que a função do
 * banco monta: agrega a última mensagem e junta DRE, credenciado e OS.
 */
function montarLinha(comentario: ComentarioDaTratativa): TratativaLinha | null {
  const os = ordemDa(comentario);
  if (!os) {
    return null;
  }

  const dre = MOCK_DRES.find((d) => d.id === os.dreId);
  const fala = ultimaFala(comentario);

  return {
    id: comentario.id,
    ordemServicoId: comentario.ordemServicoId,
    ano: comentario.ano,
    mes: comentario.mes,
    sublinha: comentario.sublinha,
    situacao: comentario.situacao,
    autorPapel: comentario.autorPapel,
    criadoEm: comentario.criadoEm,
    resolvidoEm: comentario.resolvidoEm,
    dreId: os.dreId,
    dreNome: dre?.nome ?? '',
    dreCodigo: dre?.codigo ?? '',
    credenciadoId: os.credenciadoId,
    credenciadoNome: os.credenciadoNome,
    credenciadoDocumento: os.credenciadoDocumento,
    osNumero: os.osNumero,
    ultimaMensagem: fala.mensagem,
    ultimaMensagemEm: fala.em,
    ultimaMensagemAutorPapel: fala.autorPapel,
  };
}

function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Aplica o recorte que a função do banco aplica: DRE e busca por credenciado,
 * documento ou número da OS. A situação NÃO entra aqui — a tela precisa da
 * lista inteira para contar os indicadores, e filtra depois.
 */
function filtrarTratativas(filtro: FiltroDeTratativas): TratativaLinha[] {
  const busca = filtro.busca ? normalizar(filtro.busca) : '';

  return COMENTARIOS_MOCK.map(montarLinha)
    .filter((linha): linha is TratativaLinha => linha !== null)
    .filter((linha) => (filtro.dreId ? linha.dreId === filtro.dreId : true))
    .filter((linha) =>
      busca
        ? normalizar(linha.credenciadoNome).includes(busca) ||
          normalizar(linha.credenciadoDocumento).includes(busca) ||
          normalizar(linha.osNumero).includes(busca)
        : true,
    )
    .sort((a, b) => b.ultimaMensagemEm.localeCompare(a.ultimaMensagemEm));
}

/**
 * A lista de tratativas do recorte, da mais recente para a mais antiga.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
export function useTratativas(
  filtro: FiltroDeTratativas,
  opcoes: { habilitado?: boolean } = {},
): UseQueryResult<TratativaLinha[], ErroApi> {
  return useQuery<TratativaLinha[], ErroApi>({
    queryKey: CHAVES_DE_TRATATIVAS.lista(filtro),
    /*
      `habilitado` existe por causa do recorte por diretoria, que só a API vai
      saber fazer: quem não é da SME não pode receber esta lista enquanto ela
      vier inteira. Desligar a consulta, e não filtrar depois, é o que impede
      que o dado de todas as DREs chegue ao navegador de quem só pode ver uma.
    */
    enabled: opcoes.habilitado ?? true,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => filtrarTratativas(filtro),
    placeholderData: (anterior) => anterior,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * A conversa de uma tratativa: todos os comentários da mesma OS, competência e
 * sublinha — a âncora que o banco usa.
 *
 * Deriva do MESMO array que alimenta a listagem. Clicar numa linha e ver outra
 * mensagem seria pior que não ter detalhe nenhum.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
export function useConversaDaTratativa(
  chave: ChaveDaConversa | null,
): UseQueryResult<ComentarioDaTratativa[], ErroApi> {
  return useQuery<ComentarioDaTratativa[], ErroApi>({
    queryKey: CHAVES_DE_TRATATIVAS.conversa(
      chave ?? { ordemServicoId: '', ano: 0, mes: 0, sublinha: '' },
    ),
    enabled: chave !== null,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () =>
      COMENTARIOS_MOCK.filter(
        (comentario) =>
          comentario.ordemServicoId === chave?.ordemServicoId &&
          comentario.ano === chave?.ano &&
          comentario.mes === chave?.mes &&
          comentario.sublinha === chave?.sublinha,
      ).sort((a, b) => a.criadoEm.localeCompare(b.criadoEm)),
    retry: naoInsistirEmRecusa,
  });
}

/** Conta quantas tratativas há em cada situação — alimenta os indicadores do topo. */
export function contarPorSituacao(linhas: TratativaLinha[]): Record<SituacaoDaTratativa, number> {
  return {
    ABERTO: linhas.filter((linha) => linha.situacao === 'ABERTO').length,
    RESPONDIDO: linhas.filter((linha) => linha.situacao === 'RESPONDIDO').length,
    RESOLVIDO: linhas.filter((linha) => linha.situacao === 'RESOLVIDO').length,
  };
}
